import { useEffect, useRef } from 'react';

/*
 * useScrollReveal — IntersectionObserver-based viewport reveal.
 *
 * The element starts hidden + offset (via the .velaigal-on-scroll class
 * defined in index.css). When it enters the viewport above the configured
 * threshold, this hook adds .is-visible and the CSS transition runs.
 *
 * By design, the reveal fires ONCE per element. Re-triggering on scroll
 * up/down would be distracting and off-brand for an editorial product.
 *
 * Usage:
 *
 *   const ref = useScrollReveal<HTMLDivElement>();
 *   return <div ref={ref} className="velaigal-on-scroll">…</div>
 *
 * The .velaigal-on-scroll class is required — the hook only flips the
 * .is-visible state. Stagger sibling reveals with .velaigal-stagger-1
 * through .velaigal-stagger-8 utility classes.
 *
 * Reduced-motion users see the content at its natural state with no
 * animation — the class system in index.css handles that automatically.
 */
export function useScrollReveal<T extends HTMLElement>(options?: {
  threshold?: number;
  rootMargin?: string;
}) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // No IntersectionObserver in the environment (SSR, very old browser):
    // just reveal immediately so the content is visible.
    if (typeof IntersectionObserver === 'undefined') {
      node.classList.add('is-visible');
      return;
    }

    // Respect the user's reduced-motion preference: skip the observer
    // entirely and reveal immediately. The CSS already neutralizes the
    // transition under prefers-reduced-motion, but adding .is-visible
    // synchronously avoids any FOUC from the initial hidden state.
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches) {
      node.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      },
      {
        threshold: options?.threshold ?? 0.15,
        rootMargin: options?.rootMargin ?? '0px 0px -10% 0px',
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [options?.threshold, options?.rootMargin]);

  return ref;
}
